import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Search, Eye, Plus, Edit, Trash2 } from 'lucide-react';
import CustomerDetails from '../components/CustomerDetails';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';

const Customers = ({ user }) => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [orders, setOrders] = useState([]);
  const [sales, setSales] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [editCustomerData, setEditCustomerData] = useState({});
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [newCustomer, setNewCustomer] = useState({
    shopName: '',
    city: '',
    phoneNumber: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [customersSnapshot, ordersSnapshot, salesSnapshot] = await Promise.all([
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'sales'))
      ]);
      
      const customersData = customersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const ordersData = ordersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(order => !order.deleted);
      const salesData = salesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })).filter(sale => !sale.deleted);
      
      setCustomers(customersData);
      setOrders(ordersData);
      setSales(salesData);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await updateDoc(doc(db, 'customers', editingCustomer.id), editCustomerData);
        setCustomers(customers.map(c => c.id === editingCustomer.id ? {...c, ...editCustomerData} : c));
        setEditingCustomer(null);
        setEditCustomerData({});
      } else {
        const customer = {
          ...newCustomer,
          createdAt: new Date().toISOString()
        };
        
        const docRef = await addDoc(collection(db, 'customers'), customer);
        const newCustomerWithId = { id: docRef.id, ...customer };
        
        setCustomers([...customers, newCustomerWithId]);
      }
      
      setNewCustomer({ shopName: '', city: '', phoneNumber: '' });
      setShowAddModal(false);
    } catch (error) {
      console.error('Error saving customer:', error);
    }
  };

  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setEditCustomerData({...customer});
    setShowAddModal(true);
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Are you sure you want to delete this customer?')) return;
    
    try {
      await deleteDoc(doc(db, 'customers', customerId));
      setCustomers(customers.filter(c => c.id !== customerId));
    } catch (error) {
      console.error('Error deleting customer:', error);
    }
  };

  if (loading) {
    return (
      <div className="inventory-page">
        <div style={{ textAlign: 'center', padding: '60px 20px' }}>
          <h3>Loading...</h3>
        </div>
      </div>
    );
  }

  const filteredCustomers = customers.filter(c => 
    c.shopName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPaginatedData = (data) => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return data.slice(startIndex, startIndex + itemsPerPage);
  };

  const getTotalPages = (data) => Math.ceil(data.length / itemsPerPage);

  return (
    <div className="inventory-page">
      <div className="inventory-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button className="btn btn-secondary" onClick={() => navigate('/dashboard')}>
            <ArrowLeft size={16} style={{ marginRight: '8px' }} />Back
          </button>
          <h1 className="inventory-title">Customer Management</h1>
        </div>
        
        <div className="inventory-actions">
          <div style={{ position: 'relative' }}>
            <Search size={16} style={{ position: 'absolute', left: '15px', top: '50%', transform: 'translateY(-50%)', color: '#666' }} />
            <input
              type="text"
              placeholder="Search customers..."
              className="search-bar"
              style={{ paddingLeft: '45px' }}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <button className="btn btn-primary" onClick={() => setShowAddModal(true)}>
            <Plus size={16} style={{ marginRight: '8px' }} />Add Customer
          </button>
        </div>
      </div>

      <div style={{ padding: '20px 30px', background: '#f8f9fa' }}>
        <div className="inventory-grid">
          {getPaginatedData(filteredCustomers).map(customer => {
            const customerOrders = orders.filter(o => o.customerId === customer.id);
            const customerSales = sales.filter(s => s.customerId === customer.id);
            const totalSpent = customerSales.reduce((sum, sale) => sum + (sale.totalAmount || sale.total || 0), 0);
            
            return (
              <div key={customer.id} className="inventory-item" style={{
                background: 'white',
                borderRadius: '12px',
                padding: '20px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                border: '1px solid #e9ecef'
              }}>
                <div className="item-header">
                  <div className="item-article">{customer.shopName}</div>
                  <div className="item-stock" style={{ color: '#667eea', fontSize: '0.8rem' }}>
                    {customer.city}
                  </div>
                </div>
                <div className="item-details">
                  <div className="item-detail">
                    <span className="item-detail-label">Phone:</span>
                    <span className="item-detail-value">{customer.phoneNumber || 'N/A'}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Orders:</span>
                    <span className="item-detail-value">{customerOrders.length}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Sales:</span>
                    <span className="item-detail-value">{customerSales.length}</span>
                  </div>
                  <div className="item-detail">
                    <span className="item-detail-label">Total Spent:</span>
                    <span className="item-detail-value">LKR {totalSpent.toLocaleString()}</span>
                  </div>
                </div>
                
                {customerOrders.length > 0 && (
                  <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '8px' }}>
                    <h5 style={{ margin: '0 0 10px 0', fontSize: '0.9rem' }}>Recent Orders:</h5>
                    {customerOrders.slice(0, 3).map((order, index) => (
                      <div key={index} style={{ fontSize: '0.8rem', marginBottom: '5px', color: '#666' }}>
                        #{String(order.id).slice(-6).padStart(6, '0')} - {order.status.toUpperCase()} - LKR {(order.total || 0).toLocaleString()}
                      </div>
                    ))}
                    {customerOrders.length > 3 && (
                      <div style={{ fontSize: '0.7rem', color: '#999' }}>...and {customerOrders.length - 3} more</div>
                    )}
                  </div>
                )}
                
                <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #eee' }}>
                  <button onClick={() => setSelectedCustomer(customer)} style={{ background: '#3498db', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Eye size={14} /> View
                  </button>
                  <button onClick={() => handleEditCustomer(customer)} style={{ background: '#f39c12', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Edit size={14} /> Edit
                  </button>
                  <button onClick={() => handleDeleteCustomer(customer.id)} style={{ background: '#e74c3c', color: 'white', border: 'none', padding: '8px 12px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <Trash2 size={14} /> Delete
                  </button>
                </div>
              </div>
            );
          })}
        </div>
        
        {getTotalPages(filteredCustomers) > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px' }}>
            <button 
              onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
              disabled={currentPage === 1}
              className="btn btn-secondary"
            >
              Previous
            </button>
            <span style={{ padding: '10px 20px', background: 'white', borderRadius: '8px' }}>
              Page {currentPage} of {getTotalPages(filteredCustomers)}
            </span>
            <button 
              onClick={() => setCurrentPage(Math.min(getTotalPages(filteredCustomers), currentPage + 1))}
              disabled={currentPage === getTotalPages(filteredCustomers)}
              className="btn btn-secondary"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Add Customer Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={() => {
          setShowAddModal(false);
          setEditingCustomer(null);
          setEditCustomerData({});
        }}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">{editingCustomer ? 'Edit Customer' : 'Add New Customer'}</h2>
            <form onSubmit={handleAddCustomer} className="modal-form">
              <div className="form-group">
                <label>Shop Name</label>
                <input 
                  type="text" 
                  value={editingCustomer ? editCustomerData.shopName : newCustomer.shopName} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, shopName: e.target.value}) :
                    setNewCustomer({...newCustomer, shopName: e.target.value})
                  } 
                  placeholder="Enter shop name"
                  required 
                />
              </div>
              <div className="form-group">
                <label>City</label>
                <input 
                  type="text" 
                  value={editingCustomer ? editCustomerData.city : newCustomer.city} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, city: e.target.value}) :
                    setNewCustomer({...newCustomer, city: e.target.value})
                  } 
                  placeholder="Enter city"
                  required 
                />
              </div>
              <div className="form-group">
                <label>Phone Number (Optional)</label>
                <input 
                  type="text" 
                  value={editingCustomer ? (editCustomerData.phoneNumber || '') : newCustomer.phoneNumber} 
                  onChange={(e) => editingCustomer ? 
                    setEditCustomerData({...editCustomerData, phoneNumber: e.target.value}) :
                    setNewCustomer({...newCustomer, phoneNumber: e.target.value})
                  } 
                  placeholder="Enter phone number"
                />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => {
                  setShowAddModal(false);
                  setEditingCustomer(null);
                  setEditCustomerData({});
                }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{editingCustomer ? 'Update Customer' : 'Add Customer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      
      {selectedCustomer && (
        <CustomerDetails 
          customer={selectedCustomer} 
          onClose={() => setSelectedCustomer(null)} 
        />
      )}
    </div>
  );
};

export default Customers;